import { LoginForm } from "@features/auth/login-form";
import { env } from "@shared/config/env";
import { Card } from "@shared/ui/card";
import { Activity, BarChart3, Database, LogIn, Users } from "lucide-react";
import { FormattedMessage } from "react-intl";

export default function LoginPage() {
	return (
		<main className="min-h-screen flex items-center justify-center bg-muted p-6 sm:p-10">
			<div className="w-full h-full max-w-5xl grid gap-8 lg:grid-cols-2 lg:gap-12 items-center">
				<section className="w-full max-w-md mx-auto lg:mx-0">
					<div className="mb-8 flex items-center gap-2">
						<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
							<LogIn className="h-5 w-5" />
						</div>
						<span className="text-lg font-semibold tracking-tight">
							<FormattedMessage
								id="project.name"
								defaultMessage="{name} Panel"
								values={{
									name: env.VITE_PROJECT_NAME,
								}}
							/>
						</span>
					</div>

					<h1 className="text-3xl font-bold tracking-tight">
						<FormattedMessage id="form.login.title" defaultMessage="Log in" />
					</h1>
					<p className="mt-2 text-sm text-muted-foreground">
						<FormattedMessage
							id="form.login.description"
							defaultMessage="Enter your login credentials to continue."
						/>
					</p>

					<LoginForm />
					<p className="mt-8 text-center text-xs text-muted-foreground">
						<FormattedMessage
							id="project.rights"
							defaultMessage="© {date} {name} Panel. All rights reserved"
							values={{
								date: new Date().getFullYear(),
								name: env.VITE_PROJECT_NAME,
							}}
						/>
					</p>
				</section>

				<aside className="hidden lg:block w-full max-w-md">
					<Card className="w-full p-5 shadow-2xl">
						<div className="flex items-center justify-between border-b border-border pb-3">
							<div className="flex items-center gap-2">
								<div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/15 text-primary">
									<Database className="h-4 w-4" />
								</div>
								<span className="text-sm font-semibold">
									<FormattedMessage
										id="page.login.overview.title"
										defaultMessage="Project Overview"
									/>
								</span>
							</div>
							<div className="flex gap-1.5">
								<span className="h-2 w-2 rounded-full bg-primary" />
								<span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
								<span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
							</div>
						</div>

						<div className="mt-4 grid grid-cols-3 gap-3">
							<div className="rounded-lg border border-border bg-secondary/40 p-3">
								<Users className="h-4 w-4 text-primary" />
								<p className="mt-2 text-xs text-muted-foreground">
									<FormattedMessage id="page.login.overview.users" defaultMessage="Users" />
								</p>
								<p className="text-sm font-semibold">12.4k</p>
							</div>
							<div className="rounded-lg border border-border bg-secondary/40 p-3">
								<Activity className="h-4 w-4 text-primary" />
								<p className="mt-2 text-xs text-muted-foreground">
									<FormattedMessage
										id="page.login.overview.requests"
										defaultMessage="Requests"
									/>
								</p>
								<p className="text-sm font-semibold">98%</p>
							</div>
							<div className="rounded-lg border border-border bg-secondary/40 p-3">
								<BarChart3 className="h-4 w-4 text-primary" />
								<p className="mt-2 text-xs text-muted-foreground">
									<FormattedMessage id="page.login.overview.growth" defaultMessage="Growth" />
								</p>
								<p className="text-sm font-semibold">+24%</p>
							</div>
						</div>

						<div className="mt-4 rounded-lg border border-border bg-secondary/40 p-3">
							<div className="flex items-center justify-between">
								<span className="text-xs text-muted-foreground">
									<FormattedMessage
										id="page.login.overview.activity"
										defaultMessage="Realtime activity"
									/>
								</span>
								<span className="text-xs text-primary">
									<FormattedMessage id="page.login.overview.live" defaultMessage="Live" />
								</span>
							</div>
							<svg viewBox="0 0 200 60" className="mt-2 h-16 w-full" preserveAspectRatio="none">
								<title>Dd</title>
								<defs>
									<linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
										<stop offset="0%" stopColor="oklch(0.74 0.17 158)" stopOpacity="0.5" />
										<stop offset="100%" stopColor="oklch(0.74 0.17 158)" stopOpacity="0" />
									</linearGradient>
								</defs>
								<path
									d="M0,45 L20,38 L40,42 L60,28 L80,32 L100,18 L120,24 L140,12 L160,20 L180,10 L200,16 L200,60 L0,60 Z"
									fill="url(#g)"
								/>
								<path
									d="M0,45 L20,38 L40,42 L60,28 L80,32 L100,18 L120,24 L140,12 L160,20 L180,10 L200,16"
									fill="none"
									stroke="oklch(0.74 0.17 158)"
									strokeWidth="1.5"
								/>
							</svg>
						</div>
					</Card>
				</aside>
			</div>
		</main>
	);
}
